import {isPlatformBrowser} from '@angular/common';
import {HttpBackend, HttpClient} from '@angular/common/http';
import {Inject, Injectable, PLATFORM_ID, inject, signal} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {firstValueFrom, timeout} from 'rxjs';
import {environment} from '../../../environments/environment';
import {isNewerProductVersion, parseProductVersion} from './product-version';
import type {WebVersionResponseDto} from './web-version-response.dto';
import {WEB_VERSION_HINT_NATIVE} from './web-version-hint-native';

const DISMISSED_VERSION_KEY = 'invemory.webVersionHint.dismissedInstalledVersion';
const PRODUCTION_WEB_URL = 'https://invemory.com';

@Injectable({providedIn: 'root'})
export class WebVersionHintService {
  private readonly native = inject(WEB_VERSION_HINT_NATIVE);
  readonly pending = signal(false);
  private readonly client: HttpClient;
  private initialized = false;
  private destroyed = false;
  private installedVersion = '';

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object, backend: HttpBackend) {
    // This public website request must not send API credentials or trigger API dialogs.
    this.client = new HttpClient(backend);
  }

  async initialize(): Promise<void> {
    if (this.initialized || !this.isProductionAndroid()) return;
    this.initialized = true;
    try {
      const info = await this.native.getInfo();
      if (!parseProductVersion(info.versionName) || !Number.isSafeInteger(info.versionCode) || info.versionCode <= 0) return;
      this.installedVersion = `${info.versionName}-build.${info.versionCode}`;
      const dismissed = await this.native.get({key: DISMISSED_VERSION_KEY});
      if (dismissed.value === this.installedVersion || this.destroyed) return;
      const response = await firstValueFrom(this.client.get<WebVersionResponseDto>(
        `${PRODUCTION_WEB_URL}/web-version.json`, {withCredentials: false},
      ).pipe(timeout(5_000)));
      if (!this.destroyed) this.pending.set(isNewerProductVersion(response?.appVersion, info.versionName));
    } catch {
      // An optional hint must stay silent offline, on invalid data, or without native storage.
    }
  }

  close(): void {
    this.pending.set(false);
  }

  async dismissInstalledVersion(): Promise<void> {
    this.close();
    if (!this.installedVersion) return;
    try {
      await this.native.set({key: DISMISSED_VERSION_KEY, value: this.installedVersion});
    } catch {
      // The hint remains closed for this launch even if saving the preference fails.
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.close();
  }

  private isProductionAndroid(): boolean {
    return isPlatformBrowser(this.platformId) && environment.production
      && environment.webAppBaseUrl === PRODUCTION_WEB_URL
      && Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  }
}
